/**
 * SubformulasList — list of subformula cards + add button + master-sync glue
 * For Media Devoted | Created By Ciro Autiero
 *
 * Adds a new subformula by appending an empty card; the master AST grows by
 * one subref so the new piece is part of the group score by default. Removing
 * a card prunes any subref(s) to it from the master.
 */

import { FC, useMemo } from 'react';
import { Plus } from 'lucide-react';
import type {
  FormulaBlockNode,
  FormulaBlocksBody,
  FormulaSubformula,
} from '@trafficpulse/shared';
import {
  ensureFormulaBlocksBody,
  normalizeSubformulaWeights,
  serializeBlock,
} from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { cn } from '@/lib/utils';

import SubformulaCard from './card';
import MasterFormulaBuilder from './MasterFormulaBuilder';
import WeightTotalsWarning from '../WeightTotalsWarning';
import {
  appendSubrefToMaster,
  makeSubformula,
  pruneSubrefFromMaster,
  serializeMaster,
} from './subformula.utils';

export interface SubformulasListProps {
  body: FormulaBlocksBody | null | undefined;
  /** Used when coalescing legacy `{ ast }` blocks from bad/partial JSON. */
  migrateFallbackName?: string;
  onChange: (next: FormulaBlocksBody) => void;
}

const SubformulasList: FC<SubformulasListProps> = ({
  body,
  migrateFallbackName = 'Subformula 1',
  onChange,
}) => {
  // Normalize once per body change. ensureFormulaBlocksBody rebuilds every
  // subformula object, so calling it unmemoized handed fresh references to the
  // cards on every parent re-render (e.g. a sibling group's keystroke).
  const b = useMemo(
    () => ensureFormulaBlocksBody(body, migrateFallbackName),
    [body, migrateFallbackName],
  );
  const apply = (next: FormulaBlocksBody) => onChange(next);

  // Displayed weights: a sub's own set value, or an equal-split seed until set.
  // NOT auto-normalized on display, so a manual total can run over/under 100 —
  // the save gate (validateFormulaWeights) blocks until it's exactly 100.
  const displayWeights = useMemo(() => {
    const seed = normalizeSubformulaWeights(b.subformulas);
    const out: Record<string, number> = {};
    for (const s of b.subformulas) {
      out[s.id] = typeof s.weight === 'number' ? s.weight : (seed[s.id] ?? 0);
    }
    return out;
  }, [b.subformulas]);

  const showWeights = b.subformulas.length > 1;
  const total = Object.values(displayWeights).reduce((sum, v) => sum + v, 0);
  const invalid = showWeights && total !== 100;

  // Manual: set ONLY this sub's weight. Freeze every sibling at its currently
  // shown value (so nothing silently rebalances) — no proportional move.
  const changeWeight = (id: string, target: number) => {
    const clamped = Math.max(
      0,
      Math.min(100, Number.isFinite(target) ? Math.round(target) : 0),
    );
    const nextWeights = { ...displayWeights, [id]: clamped };
    apply({
      ...b,
      subformulas: b.subformulas.map((s) => ({ ...s, weight: nextWeights[s.id] ?? 0 })),
    });
  };

  const addSubformula = () => {
    const nextSub = makeSubformula(`Subformula ${b.subformulas.length + 1}`);
    const master = appendSubrefToMaster(b.master, nextSub.id);
    apply({
      subformulas: [...b.subformulas, nextSub],
      master,
      masterExpression: serializeMaster(master),
    });
  };

  const updateSub = (id: string, patch: Partial<FormulaSubformula>) => {
    apply({
      ...b,
      subformulas: b.subformulas.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    });
  };

  const updateSubAst = (id: string, ast: FormulaBlockNode | null) => {
    updateSub(id, { ast, expression: serializeBlock(ast) });
  };

  const removeSub = (id: string) => {
    const master = pruneSubrefFromMaster(b.master, id);
    apply({
      subformulas: b.subformulas.filter((s) => s.id !== id),
      master,
      masterExpression: serializeMaster(master),
    });
  };

  const updateMaster = (master: FormulaBlockNode | null) => {
    apply({ ...b, master, masterExpression: serializeMaster(master) });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {b.subformulas.map((sub) => (
          <SubformulaCard
            key={sub.id}
            sub={sub}
            weight={displayWeights[sub.id] ?? 0}
            onChange={(patch) => updateSub(sub.id, patch)}
            onAstChange={(ast) => updateSubAst(sub.id, ast)}
            onWeightChange={(next) => changeWeight(sub.id, next)}
            onRemove={() => removeSub(sub.id)}
          />
        ))}
        {showWeights && (
          <p
            className={cn(
              'text-[10.5px] transition-colors',
              invalid ? 'font-medium text-warning' : 'text-muted-foreground',
            )}
          >
            Each sub-formula's weight is its share of the group — set them manually
            to total 100% (now <span className="tabular-nums">{total}%</span>).
          </p>
        )}
        {showWeights && (
          <WeightTotalsWarning
            issues={invalid ? [{ label: 'Sub-formula weights', total }] : []}
          />
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addSubformula}
          className="h-8 px-3 text-xs"
        >
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
          Add subformula
        </Button>
      </div>
      <MasterFormulaBuilder
        master={b.master}
        masterExpression={b.masterExpression}
        subformulas={b.subformulas}
        onMasterChange={updateMaster}
      />
    </div>
  );
};

export default SubformulasList;
